import { useState } from 'react';

import {
  Text,
  SimpleGrid,
  Box,
  Button,
  Collapse,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

import { useAppDispatch } from 'hooks';

import FormInput from 'components/fields/home/FormInput';
import RadioInput from 'components/fields/home/RadioInput';
import DateInput from 'components/fields/home/DateInput';
import SelectInput from 'components/fields/home/SelectInput';

import getDateFromString from 'helpers/getDateFromString';
import getDateString from 'helpers/getDateString';
import getRadioYesNo from 'helpers/getRadioYesNo';

import { changeDwellingRatingInformation } from 'reducers/homePersonalFormReducer';

import { SECTIONS } from 'views/home_form/constants';

import ElectricalSystem from './ElectricalSystem';
import HeatingSystem from './HeatingSystem';
import PlumbingSystem from './PlumbingSystem';
import WaterMitigationSystem from './WaterMitigationSystem';
import InteriorConstruction from './InteriorConstruction';
import AdditionalFeatures from './AdditionalFeatures';

import type { APIPersonalHomeApplicationForm } from 'types/api.types';
import useIsBinderDisabled from 'hooks/useFormBinderDisabled';

interface RatingInformationProps {
  dwelling: APIPersonalHomeApplicationForm['dwellings'][0];
  index: number;
}

const RatingInformation = ({ dwelling, index }: RatingInformationProps) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const dispatch = useAppDispatch();
  const isDisabled = useIsBinderDisabled('homePersonalForm');

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bgColor = useColorModeValue('', 'gray.800');
  const shadowColor = useColorModeValue(
    '0 2px 4px rgba(0,0,0,0.1)',
    '0 2px 4px rgba(0,0,0,0.3)',
  );

  const onInputChange = (event: any) => {
    const { name, value } = event.target;
    dispatch(changeDwellingRatingInformation({ index, name, value }));
  };

  const onRadioChange = (value: string, name: string) => {
    dispatch(
      changeDwellingRatingInformation({
        index,
        name,
        value: value === 'true',
      }),
    );
  };

  const onDateChange = (date: Date, name: string) => {
    dispatch(
      changeDwellingRatingInformation({
        index,
        name,
        value: getDateString(date),
      }),
    );
  };

  const ratingInfo = dwelling.rating_information;

  if (!ratingInfo) return null;

  return (
    <Box
      border="1px solid"
      borderColor={borderColor}
      borderRadius="md"
      bg={bgColor}
      boxShadow={shadowColor}
      overflow="hidden"
      mb="20px"
    >
      <Box
        rounded="5px"
        p="10px"
        backgroundColor="brand.400"
        color="white"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box fontWeight="medium">Rating Information</Box>
        <Button
          variant="ghost"
          size="sm"
          color="white"
          _hover={{ bg: 'whiteAlpha.200' }}
          onClick={() => setIsCollapsed(!isCollapsed)}
          rightIcon={
            <Icon as={isCollapsed ? ChevronDownIcon : ChevronUpIcon} />
          }
        >
          {isCollapsed ? 'Expand' : 'Collapse'}
        </Button>
      </Box>

      <Collapse in={!isCollapsed} animateOpacity>
        <Box p="6">
          {/* Basic Building Information */}
          <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
            Basic Building Information
          </Text>

          <SimpleGrid columns={6} spacing="15px" mb="20px">
            <FormInput
              section={SECTIONS.DWELLINGS}
              label="Year Built"
              name="year_built"
              value={ratingInfo.year_built || ''}
              onChange={onInputChange}
              disabled
            />
            <FormInput
              section={SECTIONS.DWELLINGS}
              label="Number of Storeys"
              name="number_of_storeys"
              value={ratingInfo.number_of_storeys || ''}
              onChange={onInputChange}
              disabled
            />
            <FormInput
              section={SECTIONS.DWELLINGS}
              label="Number of Families"
              name="number_of_families"
              value={ratingInfo.number_of_families || ''}
              onChange={onInputChange}
              disabled
            />
            <FormInput
              section={SECTIONS.DWELLINGS}
              label="Number of Units"
              name="number_of_units"
              value={ratingInfo.number_of_units || ''}
              onChange={onInputChange}
              disabled
            />
            <FormInput
              section={SECTIONS.DWELLINGS}
              label="Access Type"
              name="access_type"
              value={ratingInfo.access_type || ''}
              onChange={onInputChange}
            />
            <RadioInput
              section={SECTIONS.DWELLINGS}
              label="Has Smokers"
              options={[
                { value: 'true', label: 'Yes' },
                { value: 'false', label: 'No' },
              ]}
              value={getRadioYesNo(ratingInfo.has_smokers)}
              onChange={(value: string) => onRadioChange(value, 'has_smokers')}
              stacked
              disabled
            />
          </SimpleGrid>

          {/* Living Area */}
          <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
            Living Area
          </Text>
          <SimpleGrid columns={6} spacing="15px" mb="20px">
            <FormInput
              section={SECTIONS.DWELLINGS}
              label="Area"
              name="total_living_area_area"
              value={ratingInfo.total_living_area.area || ''}
              onChange={(event: any) => {
                const { value } = event.target;
                dispatch(
                  changeDwellingRatingInformation({
                    index,
                    name: 'total_living_area',
                    value: {
                      ...ratingInfo.total_living_area,
                      area: value,
                    },
                  }),
                );
              }}
              disabled
            />
            <SelectInput
              section={SECTIONS.DWELLINGS}
              label="Area Type"
              name="total_living_area_type"
              options={[
                { value: '', label: 'Select Type' },
                { value: 'sqft', label: 'Square Feet' },
                { value: 'sqm', label: 'Square Meters' },
              ]}
              value={ratingInfo.total_living_area.type || ''}
              onChange={(event: any) => {
                const { value } = event.target;
                dispatch(
                  changeDwellingRatingInformation({
                    index,
                    name: 'total_living_area',
                    value: {
                      ...ratingInfo.total_living_area,
                      type: value,
                    },
                  }),
                );
              }}
              disabled
            />
          </SimpleGrid>

          {/* Occupant Information */}
          <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
            Occupant Information
          </Text>
          <SimpleGrid templateColumns="1.5fr 2fr 2fr" spacing="15px" mb="20px">
            <DateInput
              section={SECTIONS.DWELLINGS}
              label="Date of Birth (Eldest Occupant)"
              date={getDateFromString(ratingInfo.date_of_birth_eldest_occupant)}
              onChange={(date: Date) =>
                onDateChange(date, 'date_of_birth_eldest_occupant')
              }
            />
            <FormInput
              section={SECTIONS.DWELLINGS}
              label="Relationship to Applicant"
              name="relationship_to_applicant"
              value={ratingInfo.relationship_to_applicant || ''}
              onChange={onInputChange}
            />
            <FormInput
              section={SECTIONS.DWELLINGS}
              label="Occupancy Type"
              name="occupancy_type"
              value={ratingInfo.occupancy_type || ''}
              onChange={onInputChange}
            />
          </SimpleGrid>

          {/* Structure Information */}
          <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
            Structure Information
          </Text>
          <SimpleGrid columns={6} spacing="15px" mb="20px">
            <FormInput
              section={SECTIONS.DWELLINGS}
              label="Structure Type"
              name="structure_type"
              value={ratingInfo.structure_type || ''}
              onChange={onInputChange}
              disabled
            />
            <FormInput
              section={SECTIONS.DWELLINGS}
              label="Foundation Type"
              name="foundation_type"
              value={ratingInfo.foundation_type || ''}
              onChange={onInputChange}
              disabled
            />
            <FormInput
              section={SECTIONS.DWELLINGS}
              label="Finished Basement %"
              name="finished_basement_percent"
              value={ratingInfo.finished_basement_percent || ''}
              onChange={onInputChange}
              disabled
            />
            <FormInput
              section={SECTIONS.DWELLINGS}
              label="Exterior Wall Framing"
              name="exterior_wall_framing_type"
              value={ratingInfo.exterior_wall_framing_type || ''}
              onChange={onInputChange}
              disabled
            />
            <FormInput
              section={SECTIONS.DWELLINGS}
              label="Exterior Wall Finish"
              name="exterior_wall_finish_type"
              value={ratingInfo.exterior_wall_finish_type || ''}
              onChange={onInputChange}
              disabled
            />
            <FormInput
              section={SECTIONS.DWELLINGS}
              label="Roof Covering Type"
              name="roof_covering_type"
              value={ratingInfo.roof_covering_type || ''}
              onChange={onInputChange}
              disabled
            />
          </SimpleGrid>

          {/* Evaluation Information */}
          <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
            Evaluation Information
          </Text>
          <SimpleGrid columns={5} spacing="15px" mb="20px">
            <FormInput
              section={SECTIONS.DWELLINGS}
              label="Replacement Cost Evaluator Product"
              name="replacement_cost_evaluator_product"
              value={ratingInfo.replacement_cost_evaluator_product || ''}
              onChange={onInputChange}
              disabled
            />
            <DateInput
              section={SECTIONS.DWELLINGS}
              label="Date Evaluation Completed"
              date={getDateFromString(ratingInfo.date_evaluation_completed)}
              onChange={(date: Date) =>
                onDateChange(date, 'date_evaluation_completed')
              }
              disabled
            />
          </SimpleGrid>

          {/* Roof Upgrades */}
          <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
            Roof Upgrades
          </Text>
          <SimpleGrid columns={5} spacing="15px" mb="20px">
            <FormInput
              section={SECTIONS.DWELLINGS}
              label="Full Year Upgrade"
              name="upgrades_roof_full_year"
              value={ratingInfo.upgrades.roof.full_year || ''}
              onChange={(event: any) => {
                const { value } = event.target;
                dispatch(
                  changeDwellingRatingInformation({
                    index,
                    name: 'upgrades',
                    value: {
                      ...ratingInfo.upgrades,
                      roof: {
                        ...ratingInfo.upgrades.roof,
                        full_year: value,
                      },
                    },
                  }),
                );
              }}
              disabled
            />
            <FormInput
              section={SECTIONS.DWELLINGS}
              label="Partial Year Upgrade"
              name="upgrades_roof_partial_year"
              value={ratingInfo.upgrades.roof.partial_year || ''}
              onChange={(event: any) => {
                const { value } = event.target;
                dispatch(
                  changeDwellingRatingInformation({
                    index,
                    name: 'upgrades',
                    value: {
                      ...ratingInfo.upgrades,
                      roof: {
                        ...ratingInfo.upgrades.roof,
                        partial_year: value,
                      },
                    },
                  }),
                );
              }}
              disabled
            />
          </SimpleGrid>

          {/* Interior Construction */}
          <InteriorConstruction dwelling={dwelling} index={index} />

          {/* Systems Information */}
          <ElectricalSystem dwelling={dwelling} index={index} />
          <HeatingSystem dwelling={dwelling} index={index} />
          <PlumbingSystem dwelling={dwelling} index={index} />
          <WaterMitigationSystem dwelling={dwelling} index={index} />

          {/* Additional Features */}
          <AdditionalFeatures dwelling={dwelling} index={index} />

          {/* Detached Outbuildings/Structures */}
          <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
            Detached Outbuildings/Structures
          </Text>

          {/* Display existing structures */}
          {ratingInfo.detached_outbuildings_structures &&
            ratingInfo.detached_outbuildings_structures.length > 0 && (
              <>
                {ratingInfo.detached_outbuildings_structures.map(
                  (structure: any, structureIndex: number) => (
                    <Box
                      key={structureIndex}
                      mb="15px"
                      p="15px"
                      border="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb="10px"
                      >
                        <Text
                          fontWeight="medium"
                          fontSize="sm"
                          color="gray.600"
                        >
                          Structure {structureIndex + 1}
                        </Text>
                        <Button
                          size="sm"
                          colorScheme="red"
                          variant="outline"
                          onClick={() => {
                            const updatedStructures =
                              ratingInfo.detached_outbuildings_structures.filter(
                                (_: any, idx: number) => idx !== structureIndex,
                              );
                            dispatch(
                              changeDwellingRatingInformation({
                                index,
                                name: 'detached_outbuildings_structures',
                                value: updatedStructures,
                              }),
                            );
                          }}
                        >
                          Remove
                        </Button>
                      </Box>
                      <SimpleGrid columns={6} spacing="15px" mb="15px">
                        <FormInput
                          section={SECTIONS.DWELLINGS}
                          label="Year"
                          name={`detached_outbuildings_structures_${structureIndex}_year`}
                          value={structure.year || ''}
                          onChange={(event: any) => {
                            const { value } = event.target;
                            const updatedStructures = [
                              ...ratingInfo.detached_outbuildings_structures,
                            ];
                            updatedStructures[structureIndex] = {
                              ...updatedStructures[structureIndex],
                              year: value,
                            };
                            dispatch(
                              changeDwellingRatingInformation({
                                index,
                                name: 'detached_outbuildings_structures',
                                value: updatedStructures,
                              }),
                            );
                          }}
                        />
                        <FormInput
                          section={SECTIONS.DWELLINGS}
                          label="Structure Type"
                          name={`detached_outbuildings_structures_${structureIndex}_structure_type`}
                          value={structure.structure_type || ''}
                          onChange={(event: any) => {
                            const { value } = event.target;
                            const updatedStructures = [
                              ...ratingInfo.detached_outbuildings_structures,
                            ];
                            updatedStructures[structureIndex] = {
                              ...updatedStructures[structureIndex],
                              structure_type: value,
                            };
                            dispatch(
                              changeDwellingRatingInformation({
                                index,
                                name: 'detached_outbuildings_structures',
                                value: updatedStructures,
                              }),
                            );
                          }}
                        />
                        <FormInput
                          section={SECTIONS.DWELLINGS}
                          label="Exterior Wall Framing Type"
                          name={`detached_outbuildings_structures_${structureIndex}_exterior_wall_framing_type`}
                          value={structure.exterior_wall_framing_type || ''}
                          onChange={(event: any) => {
                            const { value } = event.target;
                            const updatedStructures = [
                              ...ratingInfo.detached_outbuildings_structures,
                            ];
                            updatedStructures[structureIndex] = {
                              ...updatedStructures[structureIndex],
                              exterior_wall_framing_type: value,
                            };
                            dispatch(
                              changeDwellingRatingInformation({
                                index,
                                name: 'detached_outbuildings_structures',
                                value: updatedStructures,
                              }),
                            );
                          }}
                        />
                        <FormInput
                          section={SECTIONS.DWELLINGS}
                          label="Heating Apparatus Type"
                          name={`detached_outbuildings_structures_${structureIndex}_heating_apparatus_type`}
                          value={structure.heating_apparatus_type || ''}
                          onChange={(event: any) => {
                            const { value } = event.target;
                            const updatedStructures = [
                              ...ratingInfo.detached_outbuildings_structures,
                            ];
                            updatedStructures[structureIndex] = {
                              ...updatedStructures[structureIndex],
                              heating_apparatus_type: value,
                            };
                            dispatch(
                              changeDwellingRatingInformation({
                                index,
                                name: 'detached_outbuildings_structures',
                                value: updatedStructures,
                              }),
                            );
                          }}
                        />
                        <FormInput
                          section={SECTIONS.DWELLINGS}
                          label="Fuel Type"
                          name={`detached_outbuildings_structures_${structureIndex}_fuel_type`}
                          value={structure.fuel_type || ''}
                          onChange={(event: any) => {
                            const { value } = event.target;
                            const updatedStructures = [
                              ...ratingInfo.detached_outbuildings_structures,
                            ];
                            updatedStructures[structureIndex] = {
                              ...updatedStructures[structureIndex],
                              fuel_type: value,
                            };
                            dispatch(
                              changeDwellingRatingInformation({
                                index,
                                name: 'detached_outbuildings_structures',
                                value: updatedStructures,
                              }),
                            );
                          }}
                        />
                        <FormInput
                          section={SECTIONS.DWELLINGS}
                          label="Value"
                          name={`detached_outbuildings_structures_${structureIndex}_value`}
                          value={structure.value || ''}
                          onChange={(event: any) => {
                            const { value } = event.target;
                            const updatedStructures = [
                              ...ratingInfo.detached_outbuildings_structures,
                            ];
                            updatedStructures[structureIndex] = {
                              ...updatedStructures[structureIndex],
                              value: value,
                            };
                            dispatch(
                              changeDwellingRatingInformation({
                                index,
                                name: 'detached_outbuildings_structures',
                                value: updatedStructures,
                              }),
                            );
                          }}
                        />
                        <FormInput
                          section={SECTIONS.DWELLINGS}
                          label="Total Area (Square Feet)"
                          name={`detached_outbuildings_structures_${structureIndex}_total_area_square_feet`}
                          value={structure.total_area.square_feet || ''}
                          onChange={(event: any) => {
                            const { value } = event.target;
                            const updatedStructures = [
                              ...ratingInfo.detached_outbuildings_structures,
                            ];
                            updatedStructures[structureIndex] = {
                              ...updatedStructures[structureIndex],
                              total_area: {
                                ...updatedStructures[structureIndex].total_area,
                                square_feet: value,
                              },
                            };
                            dispatch(
                              changeDwellingRatingInformation({
                                index,
                                name: 'detached_outbuildings_structures',
                                value: updatedStructures,
                              }),
                            );
                          }}
                        />
                        <FormInput
                          section={SECTIONS.DWELLINGS}
                          label="Total Area (Square Meters)"
                          name={`detached_outbuildings_structures_${structureIndex}_total_area_square_meters`}
                          value={structure.total_area.square_meters || ''}
                          onChange={(event: any) => {
                            const { value } = event.target;
                            const updatedStructures = [
                              ...ratingInfo.detached_outbuildings_structures,
                            ];
                            updatedStructures[structureIndex] = {
                              ...updatedStructures[structureIndex],
                              total_area: {
                                ...updatedStructures[structureIndex].total_area,
                                square_meters: value,
                              },
                            };
                            dispatch(
                              changeDwellingRatingInformation({
                                index,
                                name: 'detached_outbuildings_structures',
                                value: updatedStructures,
                              }),
                            );
                          }}
                        />
                      </SimpleGrid>
                    </Box>
                  ),
                )}
              </>
            )}

          {/* Add Structure Button */}
          {(!ratingInfo.detached_outbuildings_structures ||
            ratingInfo.detached_outbuildings_structures.length < 3) && (
            <Button
              size="sm"
              colorScheme="blue"
              variant="outline"
              disabled={isDisabled}
              onClick={() => {
                const currentStructures =
                  ratingInfo.detached_outbuildings_structures || [];
                const newStructure = {
                  year: '',
                  structure_type: '',
                  exterior_wall_framing_type: '',
                  heating_apparatus_type: '',
                  fuel_type: '',
                  total_area: {
                    square_feet: '',
                    square_meters: '',
                  },
                  value: '',
                };
                dispatch(
                  changeDwellingRatingInformation({
                    index,
                    name: 'detached_outbuildings_structures',
                    value: [...currentStructures, newStructure],
                  }),
                );
              }}
            >
              Add Structure (
              {!ratingInfo.detached_outbuildings_structures
                ? 0
                : ratingInfo.detached_outbuildings_structures.length}
              /3)
            </Button>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default RatingInformation;
